import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 6);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        
        <div className="hero-container container">
          <div className={`hero-content ${isVisible ? 'fade-in' : ''}`}>
            <div className="hero-badge">
              <span className="badge-icon">✨</span>
              <span className="badge-text">AI-Powered Learning Platform</span>
            </div>
            <h1 className="hero-title">
              Transform Your English Skills with 
              <span className="gradient-text"> EnglishMaster</span>
            </h1>
            <p className="hero-description">
              Join over 1 million learners worldwide. Master vocabulary, perfect your grammar, 
              and achieve fluency with personalized lessons powered by advanced AI technology.
            </p>
            <div className="hero-buttons">
              <a href="#download" className="btn btn-primary">
                <span className="btn-icon">📱</span>
                <span>Start Learning Free</span>
              </a>
              <a href="#features" className="btn btn-secondary">
                <span className="btn-icon">▶️</span>
                <span>Watch Demo</span>
              </a>
            </div>
          </div>
          
          <div className={`hero-image ${isVisible ? 'slide-in-right' : ''}`}>
            <div className="hero-illustration">
              <div className="float-bubble bubble-1">
                <div className="bubble-content">
                  <span className="bubble-icon">🎯</span>
                  <span className="bubble-text">Daily Goals</span>
                </div>
              </div>
              <div className="float-bubble bubble-2">
                <div className="bubble-content">
                  <span className="bubble-icon">🔥</span>
                  <span className="bubble-text">7 Day Streak!</span>
                </div>
              </div>
              <div className="float-bubble bubble-3">
                <div className="bubble-content">
                  <span className="bubble-icon">✅</span>
                  <span className="bubble-text">Lesson Complete</span>
                </div>
              </div>
              
              <div className="hero-phone">
                <div className="phone-screen">
                  <div className="screen-header">
                    <div className="header-dots">
                      <span className="dot"></span>
                      <span className="dot"></span>
                      <span className="dot"></span>
                    </div>
                    <div className="header-title">EnglishMaster</div>
                    <div className="header-menu">☰</div>
                  </div>
                  <div className="screen-content">
                    <div className="welcome-card">
                      <div className="welcome-icon">👋</div>
                      <div className="welcome-text">
                        <h3>Welcome back!</h3>
                        <p>Ready for today's lesson?</p>
                      </div>
                    </div>
                    <div className="lesson-grid">
                      <div className="mini-card card-1">
                        <span className="mini-icon">📚</span>
                        <span className="mini-label">Vocabulary</span>
                      </div>
                      <div className="mini-card card-2">
                        <span className="mini-icon">✍️</span>
                        <span className="mini-label">Grammar</span>
                      </div>
                      <div className="mini-card card-3">
                        <span className="mini-icon">🗣️</span>
                        <span className="mini-label">Speaking</span>
                      </div>
                      <div className="mini-card card-4">
                        <span className="mini-icon">👂</span>
                        <span className="mini-label">Listening</span>
                      </div>
                    </div>
                    <div className="progress-section">
                      <div className="progress-header">
                        <span>Today's Progress</span>
                        <span className="progress-percent">75%</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features section">
        <div className="container">
          <div className="section-header fade-in">
            <span className="section-badge">Features</span>
            <h2 className="section-title">Everything You Need to Master English</h2>
            <p className="section-subtitle">
              Comprehensive tools and resources designed to accelerate your learning journey
            </p>
          </div>
          
          <div className="features-grid">
            <div className={`feature-card card ${activeFeature === 0 ? 'active' : ''}`}>
              <div className="feature-icon-wrapper">
                <div className="feature-icon">📚</div>
                <div className="feature-glow"></div>
              </div>
              <h3 className="feature-title">Smart Vocabulary Builder</h3>
              <p className="feature-description">
                Learn 5000+ essential words with context-based examples, pronunciation guides, 
                and intelligent spaced repetition for long-term retention.
              </p>
              <ul className="feature-list">
                <li>✓ AI-powered word recommendations</li>
                <li>✓ Visual memory aids</li>
                <li>✓ Usage in real sentences</li>
              </ul>
            </div>

            <div className={`feature-card card ${activeFeature === 1 ? 'active' : ''}`}>
              <div className="feature-icon-wrapper">
                <div className="feature-icon">✍️</div>
                <div className="feature-glow"></div>
              </div>
              <h3 className="feature-title">Interactive Grammar Lessons</h3>
              <p className="feature-description">
                Master all grammar rules from basics to advanced with interactive exercises, 
                instant feedback, and progress tracking.
              </p>
              <ul className="feature-list">
                <li>✓ 200+ grammar topics</li>
                <li>✓ Real-time corrections</li>
                <li>✓ Personalized practice</li>
              </ul>
            </div>

            <div className={`feature-card card ${activeFeature === 2 ? 'active' : ''}`}>
              <div className="feature-icon-wrapper">
                <div className="feature-icon">🗣️</div>
                <div className="feature-glow"></div>
              </div>
              <h3 className="feature-title">AI Speech Recognition</h3>
              <p className="feature-description">
                Perfect your pronunciation with cutting-edge AI that analyzes your speech 
                and provides detailed feedback on accuracy and fluency.
              </p>
              <ul className="feature-list">
                <li>✓ Native speaker comparison</li>
                <li>✓ Accent training</li>
                <li>✓ Real conversation practice</li>
              </ul>
            </div>

            <div className={`feature-card card ${activeFeature === 3 ? 'active' : ''}`}>
              <div className="feature-icon-wrapper">
                <div className="feature-icon">👂</div>
                <div className="feature-glow"></div>
              </div>
              <h3 className="feature-title">Listening Comprehension</h3>
              <p className="feature-description">
                Train your ear with thousands of audio lessons featuring native speakers 
                from different regions and various difficulty levels.
              </p>
              <ul className="feature-list">
                <li>✓ Multiple accents covered</li>
                <li>✓ Speed control options</li>
                <li>✓ Transcript included</li>
              </ul>
            </div>

            <div className={`feature-card card ${activeFeature === 4 ? 'active' : ''}`}>
              <div className="feature-icon-wrapper">
                <div className="feature-icon">📝</div>
                <div className="feature-glow"></div>
              </div>
              <h3 className="feature-title">Writing Excellence</h3>
              <p className="feature-description">
                Develop strong writing skills through guided exercises, essay prompts, 
                and AI-powered grammar and style suggestions.
              </p>
              <ul className="feature-list">
                <li>✓ Essay correction service</li>
                <li>✓ Style improvement tips</li>
                <li>✓ Writing templates</li>
              </ul>
            </div>

            <div className={`feature-card card ${activeFeature === 5 ? 'active' : ''}`}>
              <div className="feature-icon-wrapper">
                <div className="feature-icon">📊</div>
                <div className="feature-glow"></div>
              </div>
              <h3 className="feature-title">Advanced Analytics</h3>
              <p className="feature-description">
                Track your progress with detailed insights, achievement badges, 
                learning streaks, and personalized improvement recommendations.
              </p>
              <ul className="feature-list">
                <li>✓ Visual progress dashboard</li>
                <li>✓ Strength/weakness analysis</li>
                <li>✓ Achievement system</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Path Section */}
      <section className="learning-path section">
        <div className="container">
          <div className="section-header fade-in">
            <span className="section-badge">How It Works</span>
            <h2 className="section-title">Your Path to English Fluency</h2>
            <p className="section-subtitle">
              A proven 3-step methodology that gets results
            </p>
          </div>
          
          <div className="path-timeline">
            <div className="timeline-item fade-in">
              <div className="timeline-number">
                <span>01</span>
                <div className="timeline-line"></div>
              </div>
              <div className="timeline-content card">
                <div className="timeline-icon">🎯</div>
                <h3 className="timeline-title">Assess Your Level</h3>
                <p className="timeline-description">
                  Take our comprehensive 10-minute assessment powered by AI to determine your 
                  current proficiency level. We evaluate vocabulary, grammar, listening, and speaking 
                  skills to create your baseline.
                </p>
                <div className="timeline-features">
                  <span className="feature-tag">Quick & Accurate</span>
                  <span className="feature-tag">No Registration Required</span>
                  <span className="feature-tag">Instant Results</span>
                </div>
              </div>
            </div>

            <div className="timeline-item fade-in" style={{animationDelay: '0.2s'}}>
              <div className="timeline-number">
                <span>02</span>
                <div className="timeline-line"></div>
              </div>
              <div className="timeline-content card">
                <div className="timeline-icon">🎨</div>
                <h3 className="timeline-title">Get Your Custom Plan</h3>
                <p className="timeline-description">
                  Receive a personalized learning path tailored to your level, goals, and available 
                  time. Our AI adapts the curriculum to focus on your weaknesses and reinforce your strengths.
                </p>
                <div className="timeline-features">
                  <span className="feature-tag">AI-Personalized</span>
                  <span className="feature-tag">Flexible Schedule</span>
                  <span className="feature-tag">Goal-Oriented</span>
                </div>
              </div>
            </div>

            <div className="timeline-item fade-in" style={{animationDelay: '0.4s'}}>
              <div className="timeline-number">
                <span>03</span>
              </div>
              <div className="timeline-content card">
                <div className="timeline-icon">🚀</div>
                <h3 className="timeline-title">Practice & Progress</h3>
                <p className="timeline-description">
                  Engage with interactive lessons, track your daily progress, and watch your skills 
                  improve. Earn achievements, maintain streaks, and join a community of learners.
                </p>
                <div className="timeline-features">
                  <span className="feature-tag">Daily Practice</span>
                  <span className="feature-tag">Real-Time Feedback</span>
                  <span className="feature-tag">Community Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="download" className="cta section">
        <div className="cta-background">
          <div className="cta-orb orb-1"></div>
          <div className="cta-orb orb-2"></div>
        </div>
        <div className="container">
          <div className="cta-content fade-in">
            <div className="cta-badge">
              <span className="badge-icon">🎉</span>
              <span className="badge-text">Start Your Free Trial Today</span>
            </div>
            <h2 className="cta-title">Ready to Master English?</h2>
            <p className="cta-description">
              Join over 1 million learners worldwide. Start your journey to fluency with 
              a 14-day free trial. No credit card required.
            </p>
            <div className="cta-buttons">
              <button className="download-button" onClick={() => window.open('https://apps.apple.com', '_blank')}>
                <svg className="download-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <div>
                  <div className="download-label">Download on the</div>
                  <div className="download-store">App Store</div>
                </div>
              </button>
              <button className="download-button" onClick={() => window.open('https://play.google.com', '_blank')}>
                <svg className="download-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
                </svg>
                <div>
                  <div className="download-label">Get it on</div>
                  <div className="download-store">Google Play</div>
                </div>
              </button>
            </div>
            <div className="cta-features">
              <div className="cta-feature-item">
                <span className="feature-check">✓</span>
                <span>14-day free trial</span>
              </div>
              <div className="cta-feature-item">
                <span className="feature-check">✓</span>
                <span>No credit card required</span>
              </div>
              <div className="cta-feature-item">
                <span className="feature-check">✓</span>
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
